import { IconButton, Modal, Scrollbar, toast } from "@carbon6/c6-frontend-design";
import { useCallback, useId, useMemo, useState, type ReactNode } from "react";
import { evaluateTrainingPolicy } from "@/api";
import { HatModalHeader } from "@/components/modal";
import { useRelativeTimeClock } from "@/utils/useRelativeTimeClock";
import { taskValue } from "./evalParamSpecs";
import type { EvaluationTargetPolicy } from "./evaluationTargetPolicy";
import { resolveSeedPlan } from "./evalSeeds";
import { EvalPolicyTable } from "./EvalPolicyTable";
import { EvalStartConfirmationModal } from "./EvalStartConfirmationModal";
import { EvaluationParametersPanel } from "./EvaluationParametersPanel";
import { reconcileSeedScenarios } from "./reconcileScenario";
import { useEvalImageParams } from "./useEvalImageParams";
import styles from "./EvaluationParamsModal.module.css";

// 두 단계 모달. 먼저 파라미터를 설정한다. 그다음 겹쳐 뜨는 확인 다이얼로그에서 시작 요약을
// 확인한다. 확인하면 선택한 policy 마다 평가 요청을 하나씩 보낸다.
// 중복 조합을 만들지 않도록 seed 는 요청의 `seeds` 필드가 아니라 각 시나리오의 파라미터에만 넣는다.
// 대상 policy 는 여기에도 카드로 표시한다. 학습 dataset 이름이 TASK 를 미리 고르기 때문이다.

export type EvaluationParamsModalProps = {
  open: boolean;
  policies: readonly EvaluationTargetPolicy[];
  onClose: () => void;
  // policy 평가 요청이 하나 이상 성공하면 호출한다.
  onSubmitted: () => void;
};

export function EvaluationParamsModal({
  open,
  policies,
  onClose,
  onSubmitted,
}: EvaluationParamsModalProps): ReactNode {
  const titleId = useId();
  const datasetNames = useMemo(
    () =>
      policies.map((policy) => policy.datasetName).filter((name): name is string => name !== null),
    [policies],
  );
  const params = useEvalImageParams(datasetNames);
  const nowMs = useRelativeTimeClock(open);
  const [launching, setLaunching] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const handleSubmit = useCallback(() => {
    const benchmarkImage = params.imageUri;
    if (benchmarkImage === null) {
      toast.error("Evaluation에 사용할 Docker Image 를 선택해 주세요.");
      return;
    }

    const task = taskValue(params.specs, params.values);
    if (task === "") {
      toast.error("Evaluation을 실행하기 전에 TASK 를 선택해 주세요.");
      return;
    }

    // seed 입력이 유효하지 않으면 사용자용 메시지를 가진 오류를 던진다. 개수는 아래 reconcile 이
    // 반환한다. seed 를 실제로 펼치는 것은 reconcile 이다.
    try {
      resolveSeedPlan(params.values);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "SEED 값을 확인해 주세요.");
      return;
    }

    setLaunching(true);
    void (async () => {
      try {
        // seed 마다 시나리오 하나를 준비하고, 모든 policy 요청이 그 시나리오를 함께 쓴다.
        const reconciled = await reconcileSeedScenarios({
          benchmarkImage,
          values: params.values,
        });
        // 어떤 파라미터를 읽을지는 이미지가 소유한다. 그래서 파라미터 하나를 알림 없이 무시하는
        // 실행에 GPU 를 쓰기 전에 그 사실을 알린다. 이 알림은 실패가 아니라 참고다. 실행은 계속되므로
        // warning 톤을 쓴다. 상류 문구는 이 앱이 소유한 문장 안에 인용한다. 그래서 상류가 문구를
        // 바꿔도 사용자에게 전달하는 내용은 바뀌지 않는다.
        for (const warning of reconciled.warnings) {
          toast.warning(`Evaluation 설정을 확인하세요: ${warning}`);
        }
        const scenarios = reconciled.scenarios.map((scenario) => ({
          name: scenario.name,
          benchmark_image: benchmarkImage,
          params: scenario.params,
        }));

        const results = await Promise.allSettled(
          policies.map((policy) => evaluateTrainingPolicy(policy.id, { scenarios })),
        );
        const succeeded = results.filter((result) => result.status === "fulfilled").length;
        const failed = results.length - succeeded;

        if (succeeded > 0) {
          toast.success(
            `Policy ${succeeded}개의 Evaluation을 Session ${succeeded * scenarios.length}개로 시작했습니다.`,
          );
          onSubmitted();
          onClose();
        }
        if (failed > 0) {
          toast.error(`Policy ${failed}개의 Evaluation을 시작하지 못했습니다.`);
        }
      } catch (error: unknown) {
        // 시나리오 준비가 실패하면 평가 요청을 보내지 않는다.
        toast.error(
          error instanceof Error ? error.message : "Evaluation 시나리오를 준비하지 못했습니다.",
        );
      } finally {
        setLaunching(false);
      }
    })();
  }, [params, policies, onClose, onSubmitted]);

  // 비용이 낮은 사전 검증이다. 확인 요약을 잘못된 입력으로 만들지 않는다.
  // handleSubmit 은 확인할 때 다시 검증한다.
  const handleRequestConfirm = useCallback(() => {
    if (taskValue(params.specs, params.values) === "") {
      toast.error("Evaluation을 실행하기 전에 TASK 를 선택해 주세요.");
      return;
    }
    try {
      resolveSeedPlan(params.values);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "SEED 값을 확인해 주세요.");
      return;
    }
    setConfirming(true);
  }, [params.specs, params.values]);

  const canSubmit = policies.length > 0 && params.selectedTag !== "" && !launching;

  let seedCount: number | null = null;
  if (confirming) {
    try {
      seedCount = resolveSeedPlan(params.values).seeds.length;
    } catch {
      seedCount = null;
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      className={styles.dialog}
      closeOnBackdropClick={!launching}
      closeOnEscape={!launching}
      aria-labelledby={titleId}
    >
      <HatModalHeader
        title={`Policy ${policies.length}개의 Evaluation 파라미터를 설정합니다.`}
        titleId={titleId}
        close={{ disabled: launching }}
      />
      <div className={styles.body}>
        <section className={styles.policySection} aria-label="Evaluation 대상">
          <span className={styles.policyLabel}>Policy</span>
          {/* 선택이 많아도 dialog 가 계속 길어지지 않도록 표 영역만 스크롤한다. */}
          <Scrollbar className={styles.policyScroller}>
            <EvalPolicyTable policies={policies} nowMs={nowMs} />
          </Scrollbar>
        </section>
        <EvaluationParametersPanel params={params} disabled={launching} />
      </div>
      <div className={styles.footer}>
        <IconButton
          text="취소"
          type="button"
          variant="ghost"
          disabled={launching}
          onClick={onClose}
        />
        <IconButton
          text="Evaluation 실행"
          type="button"
          variant="solid"
          tone="primary"
          disabled={!canSubmit}
          onClick={handleRequestConfirm}
        />
      </div>
      {confirming ? (
        <EvalStartConfirmationModal
          open
          policies={policies}
          selectedTag={params.selectedTag}
          imageUri={params.imageUri}
          specs={params.specs}
          values={params.values}
          seedCount={seedCount}
          nowMs={nowMs}
          busy={launching}
          onConfirm={handleSubmit}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </Modal>
  );
}
